import { useOrganizationId } from '@features/user';
import { useCopyToClipboard } from '@hooks/useCopyToClipboard';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Alert, Button, Link, Paper, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useAvailabilityRulesQuery } from '../store/api';

/** The practice's public self-serve booking URL
 * ({forms domain}/book/{organization_id}), presented as a settings section
 * so owners can copy it or drop it into their website. Lives on the
 * Scheduling settings page (moved from the calendar header, GDN-2414). */
export function BookingPageLinkSection() {
  const organizationId = useOrganizationId();
  const copy = useCopyToClipboard();
  const { schedulingSettings } = useEventTracking();
  // Readiness: without at least one open-hours rule (any scope), the page
  // renders but every calendar is empty — patients can't actually book.
  const { data: rulesData, isLoading: rulesLoading } =
    useAvailabilityRulesQuery({ perPage: 200 });
  const hasOpenHours = (rulesData?.items ?? []).some(
    rule => rule.effect === 'open',
  );

  if (!organizationId) {
    return null;
  }

  const formsBaseUrl =
    (import.meta.env.VITE_FORMS_URL as string | undefined) ||
    'https://forms.jotpsych.com';
  const bookingUrl = `${formsBaseUrl}/book/${organizationId}`;

  const handleCopy = () => {
    copy(bookingUrl, 'Booking page link');
    schedulingSettings.bookingPageLinkCopied();
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2}>
        <div>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Your booking page
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Patients book directly from this link — share it with them or add
            it to your website.
          </Typography>
        </div>
        {!rulesLoading &&
          (hasOpenHours ? (
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ alignItems: 'center', color: 'success.main' }}
            >
              <CheckCircleOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2">
                Accepting online bookings
              </Typography>
            </Stack>
          ) : (
            <Alert severity="warning">
              Patients can&apos;t book yet — you have no open hours. Add them
              in Availability below.
            </Alert>
          ))}
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Link
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              component="span"
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {bookingUrl}
            </Typography>
            <OpenInNewOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
          </Link>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ContentCopyOutlinedIcon />}
            onClick={handleCopy}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Copy link
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
